import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, Text } from '@frontend/design-system';
import { EIVDashboardTrialWidgetTrackingIds } from '../../../../../constants/tracking-ids';

type OnboardingModalProps = {
  modalProps: ModalControlModalProps;
  onboaringModalID: ModalID;
};

export type ModalID = 'CONNECT_PAYERS' | 'FAVORITE_CDT_CODES' | 'SET_UP_DELTA_DENTAL_MFA';

interface OnboardingStep {
  onboaringModalID: ModalID;
  title: string;
  description: string;
  videoUrl: string;
  whatYouWillLearn: string[];
  primaryCTA: {
    label: string;
    action: SettingsRoutes;
    trackingId: string;
  };
  secondaryCTA: {
    label: string;
    action: string;
    trackingId: string;
  };
}

export const onboardingModalsData: Record<ModalID, OnboardingStep> = {
  CONNECT_PAYERS: {
    onboaringModalID: 'CONNECT_PAYERS',
    title: i18next.t('Connect Payers', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
    description: i18next.t('Link your insurance providers to enable real-time eligibility verification.', {
      ns: 'featurePromotion',
      keyPrefix: 'weave-insurance-eligibility',
    }),
    videoUrl: `https://fast.wistia.com/embed/medias/s9gfc55at3?playerColor=${theme.colors.primary60}`,
    whatYouWillLearn: [
      i18next.t('How to connect and authenticate with insurance payers', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
      i18next.t('Setting up Delta Dental, Blue Cross, and other major providers', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
      i18next.t('Managing payer credentials and connection status', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
    ],
    primaryCTA: {
      label: i18next.t('Go to Payer Management', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
      action: '/insurance/payer-connection-management',
      trackingId: EIVDashboardTrialWidgetTrackingIds.gotoConnectPayersAction,
    },
    secondaryCTA: {
      label: i18next.t('Learn More', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
      action: 'https://www.weavehelp.com/weavehelp/article/weave-insurance-verification-enhanced-eligibility',
      trackingId: EIVDashboardTrialWidgetTrackingIds.learnMoreConnectPayersAction,
    },
  },
  FAVORITE_CDT_CODES: {
    onboaringModalID: 'FAVORITE_CDT_CODES',
    title: i18next.t('Favorite CDT Codes', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
    description: i18next.t('Mark frequently-used CDT codes and request specialty codes.', {
      ns: 'featurePromotion',
      keyPrefix: 'weave-insurance-eligibility',
    }),
    videoUrl: `https://fast.wistia.com/embed/medias/ln2u8cyyfw?playerColor=${theme.colors.primary60}`,
    whatYouWillLearn: [
      i18next.t('Marking frequently-used CDT codes as favorites', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
      i18next.t('Requesting specialty CDT codes not currently available', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
      i18next.t('Organizing codes by category and procedure type', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
    ],
    primaryCTA: {
      label: i18next.t('Manage CDT Codes', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
      action: '/insurance/cdt-code-management',
      trackingId: EIVDashboardTrialWidgetTrackingIds.gotoFavoriteCDTCodesAction,
    },
    secondaryCTA: {
      label: i18next.t('Learn More', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
      action:
        'https://www.weavehelp.com/weavehelp/article/weave-insurance-verification-enhanced-eligibility#Favorite_CDT_Code_Management',
      trackingId: EIVDashboardTrialWidgetTrackingIds.learnMoreFavoriteCDTCodesAction,
    },
  },
  SET_UP_DELTA_DENTAL_MFA: {
    onboaringModalID: 'SET_UP_DELTA_DENTAL_MFA',
    title: i18next.t('Setup Delta Dental MFA', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
    description: i18next.t('Secure your Delta Dental integration with multi-factor authentication.', {
      ns: 'featurePromotion',
      keyPrefix: 'weave-insurance-eligibility',
    }),
    videoUrl: `https://fast.wistia.com/embed/medias/e7blkndhiw?playerColor=${theme.colors.primary60}`,
    whatYouWillLearn: [
      i18next.t('Understanding why MFA is required for Delta Dental', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
      i18next.t('Setting up SMS, email, or authenticator app verification', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
      i18next.t('Testing and troubleshooting MFA authentication', {
        ns: 'featurePromotion',
        keyPrefix: 'weave-insurance-eligibility',
      }),
    ],
    primaryCTA: {
      label: i18next.t('Setup Automated MFA', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
      action: '/insurance/payer-connection-management',
      trackingId: EIVDashboardTrialWidgetTrackingIds.gotoMFAAction,
    },
    secondaryCTA: {
      label: i18next.t('Learn More', { ns: 'featurePromotion', keyPrefix: 'weave-insurance-eligibility' }),
      action:
        'https://www.weavehelp.com/weavehelp/article/weave-insurance-verification-enhanced-eligibility#Automated_Multi-Factor_Authentication',
      trackingId: EIVDashboardTrialWidgetTrackingIds.learnMoreMFAAction,
    },
  },
};

export const OnboardingModal = ({ modalProps, onboaringModalID }: OnboardingModalProps) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });
  const { navigate } = useSettingsNavigate();

  const data = onboardingModalsData[onboaringModalID];

  const onPrimaryCTAClick = (action: SettingsRoutes) => {
    navigate({ to: action, params: {} });
    modalProps.onClose();
  };

  const onSecondaryCTAClick = (action: string) => {
    window.open(action, '_blank', 'noopener,noreferrer');
    modalProps.onClose();
  };

  return (
    <Modal {...modalProps} maxWidth={800}>
      <Modal.Header onClose={modalProps.onClose}>{data.title}</Modal.Header>
      <Modal.Body>
        <Text size='medium'>{data.description}</Text>
        <iframe src={data.videoUrl} css={videoFrameStyles} />
        <Text weight='bold'>{t('What you will learn:')}</Text>
        <ul css={unorderedListStyles}>
          {data.whatYouWillLearn.map((point, index) => (
            <li key={index} css={listItemStyles}>
              <Icon name='check-small' color='success' />
              <Text as='span'>{point}</Text>
            </li>
          ))}
        </ul>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: data.primaryCTA.label,
          onClick: () => onPrimaryCTAClick(data.primaryCTA.action),
          trackingId: data.primaryCTA.trackingId,
        }}
        secondary={{
          label: data.secondaryCTA.label,
          onClick: () => onSecondaryCTAClick(data.secondaryCTA.action),
          trackingId: data.secondaryCTA.trackingId,
        }}
      />
    </Modal>
  );
};

const videoFrameStyles = css({
  width: '100%',
  aspectRatio: '16 / 9',
  border: 'none',
  borderRadius: theme.borderRadius.small,
  margin: `${theme.spacing(2)} 0`,
});

const unorderedListStyles = css`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const listItemStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(1)};
`;
